import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Paper,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import EnhancedKPICard from './EnhancedKPICard';
import DashboardLoadingState from '../shared/DashboardLoadingState';
import EmptyChartState from '../shared/EmptyChartState';
import { formatUtilizationVsTarget } from '../../../utils/kpiDisplayUtils';

/**
 * Unified financial KPI grid for portfolio and project-scoped views.
 * Renders the full engagement metric surface (11 KPI cards + triad + cash flow).
 *
 * @param {object} props
 * @param {boolean} props.isProjectScoped - When true, copy reflects engagement scope.
 * @param {number|null} props.cardRevenue - Scoped total revenue.
 * @param {number|null} props.cardNetProfit - Scoped net profit.
 * @param {object} props.renderableProfitabilityData - Portfolio-health payload.
 * @param {object|null} props.periodComparisonData - Period-over-period comparison.
 * @param {boolean} props.showComparison - Whether prior-period comparison is enabled.
 * @param {Function} props.isCardLoading - Endpoint loading predicate.
 * @param {Function} props.onKpiDrillDown - Drill-down handler for interactive KPIs.
 * @param {Function} props.asDisplayValue - Normalizes numeric display values.
 * @param {Function} props.formatCurrency - Currency formatter from financial hook.
 * @param {Function} props.formatPercentage - Percentage formatter from financial hook.
 * @param {Function} props.formatNullableRate - Benchmark delta formatter.
 * @param {object|null} props.utilizationData - Utilization API payload.
 * @param {object|null} props.billRateData - Bill rate API payload.
 * @param {object|null} props.realizationData - Realization API payload.
 * @param {object|null} props.wipData - WIP API payload.
 * @param {object|null} props.backlogData - Backlog API payload.
 * @param {object|null} props.contributionMarginData - Contribution margin payload.
 * @param {object|null} props.arAgingData - AR aging payload.
 * @param {object|null} props.revenueTriadData - Revenue triad payload.
 * @param {object|null} props.cashFlowData - Cash flow projection payload.
 * @param {object|null} props.expenseRatiosData - Expense ratio payload.
 * @param {object|null} props.benchmarkCompareData - Benchmark variance payload.
 * @returns {React.ReactElement} Engagement KPI grid.
 */
const FinancialEngagementKpiGrid = ({
    isProjectScoped = false,
    cardRevenue,
    cardNetProfit,
    renderableProfitabilityData,
    periodComparisonData,
    showComparison,
    isCardLoading,
    onKpiDrillDown,
    asDisplayValue,
    formatCurrency,
    formatPercentage,
    formatNullableRate,
    utilizationData,
    billRateData,
    realizationData,
    wipData,
    backlogData,
    contributionMarginData,
    arAgingData,
    revenueTriadData,
    cashFlowData,
    expenseRatiosData,
    benchmarkCompareData,
}) => {
    const scopeLabel = isProjectScoped ? 'this engagement' : 'the portfolio';

    return (
        <Grid container spacing={2} data-testid="financial-engagement-kpi-grid">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Total Revenue"
                    value={cardRevenue}
                    format="currency"
                    trend={periodComparisonData ? {
                        direction: periodComparisonData.variance.revenue_recognized_pct > 0 ? 'up' : 'down',
                        percentage: periodComparisonData.variance.revenue_recognized_pct,
                        isPositive: periodComparisonData.variance.revenue_recognized_pct > 0,
                    } : null}
                    comparison={showComparison && periodComparisonData ? {
                        value: periodComparisonData.prior.revenue_recognized,
                        label: 'Prior Period',
                        type: 'period',
                    } : null}
                    onClick={() => onKpiDrillDown('revenue')}
                    tooltip={`Total recognized revenue (ASC 606) for ${scopeLabel}`}
                    loading={isCardLoading('portfolioHealth')}
                    testId="kpi-total-revenue"
                    kpiKey="total_revenue"
                    dataSource="financial-engine/portfolio-health"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Net Profit"
                    value={cardNetProfit}
                    format="currency"
                    status={
                        renderableProfitabilityData.net_profit > 0
                            ? 'good'
                            : renderableProfitabilityData.net_profit < 0
                                ? 'critical'
                                : 'warning'
                    }
                    trend={periodComparisonData ? {
                        direction: periodComparisonData.variance.cash_collected_pct > 0 ? 'up' : 'down',
                        percentage: periodComparisonData.variance.cash_collected_pct,
                        isPositive: periodComparisonData.variance.cash_collected_pct > 0,
                    } : null}
                    onClick={() => onKpiDrillDown('revenue')}
                    tooltip="Net profit after all direct and allocated costs"
                    loading={isCardLoading('portfolioHealth')}
                    testId="kpi-net-profit"
                    kpiKey="net_profit"
                    dataSource="financial-engine/portfolio-health"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Gross Margin"
                    value={renderableProfitabilityData.profit_margin}
                    format="percentage"
                    status={
                        renderableProfitabilityData.profit_margin >= 25
                            ? 'good'
                            : renderableProfitabilityData.profit_margin >= 15
                                ? 'warning'
                                : 'critical'
                    }
                    tooltip="Gross Margin = (Revenue − Direct Costs) / Revenue"
                    loading={isCardLoading('portfolioHealth')}
                    testId="kpi-gross-margin"
                    kpiKey="gross_margin"
                    dataSource="financial-engine/portfolio-health"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Utilization"
                    value={asDisplayValue(utilizationData?.current?.average_utilization)}
                    format="percentage"
                    comparison={utilizationData?.current ? {
                        value: asDisplayValue(utilizationData.current.target_utilization),
                        label: 'Target',
                        type: 'target',
                    } : null}
                    status={(() => {
                        const util = asDisplayValue(utilizationData?.current?.average_utilization);
                        const target = asDisplayValue(utilizationData?.current?.target_utilization) ?? 80;
                        if (util === null) return null;
                        if (util >= target) return 'good';
                        if (util >= target * 0.9) return 'warning';
                        return 'critical';
                    })()}
                    statusLabel={(() => {
                        const util = asDisplayValue(utilizationData?.current?.average_utilization);
                        const target = asDisplayValue(utilizationData?.current?.target_utilization) ?? 80;
                        if (util === null) return null;
                        return formatUtilizationVsTarget(util, target);
                    })()}
                    tooltip="Billable Hours / Available Hours"
                    loading={isCardLoading('utilization')}
                    testId="kpi-utilization"
                    kpiKey="resource_utilization"
                    dataSource="resource-optimization/utilization"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Effective Bill Rate"
                    value={asDisplayValue(billRateData?.effective_bill_rate)}
                    format="currency"
                    comparison={billRateData?.scheduled_bill_rate ? {
                        value: billRateData.scheduled_bill_rate,
                        label: 'Scheduled',
                        type: 'target',
                    } : null}
                    trend={billRateData?.rate_variance ? {
                        direction: billRateData.rate_variance >= 0 ? 'up' : 'down',
                        percentage: billRateData.rate_variance,
                        isPositive: billRateData.rate_variance >= 0,
                    } : null}
                    tooltip="Effective Bill Rate = Total Billings / Billable Hours"
                    loading={isCardLoading('billRates')}
                    testId="kpi-bill-rate"
                    kpiKey="bill_rate"
                    dataSource="financial-engine/bill-rates"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Realization Rate"
                    value={asDisplayValue(realizationData?.realization_rate)}
                    format="percentage"
                    status={(() => {
                        const rate = asDisplayValue(realizationData?.realization_rate);
                        if (rate === null) return 'critical';
                        if (rate >= 0.92) return 'good';
                        if (rate >= 0.85) return 'warning';
                        return 'critical';
                    })()}
                    tooltip="Realization = Billed Hours / Recorded Billable Hours (industry median: 92%)"
                    loading={isCardLoading('realization')}
                    testId="kpi-realization-rate"
                    kpiKey="realization_rate"
                    dataSource="financial-engine/realization"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="WIP (Work in Progress)"
                    value={asDisplayValue(wipData?.wip_value ?? wipData?.total_wip)}
                    format="currency"
                    onClick={() => onKpiDrillDown('wip')}
                    tooltip="Approved, billable, uninvoiced time value. Click for aging breakdown."
                    loading={isCardLoading('wip')}
                    testId="kpi-wip-total"
                    kpiKey="wip_amount"
                    dataSource="financial-engine/wip"
                    sx={{ cursor: 'pointer' }}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <EnhancedKPICard
                    title="Backlog"
                    value={asDisplayValue(backlogData?.total_backlog)}
                    format="currency"
                    tooltip="Backlog = Contracted Value − Recognized Revenue"
                    loading={isCardLoading('backlog')}
                    testId="kpi-backlog-total"
                    kpiKey="backlog_amount"
                    dataSource="financial-engine/backlog"
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                    <Tooltip title="Contribution Margin = (Revenue − (Direct Costs + Allocated Overhead)) / Revenue">
                        <Typography variant="h6">Contribution Margin</Typography>
                    </Tooltip>
                    {isCardLoading('contributionMargin') ? (
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Loading...</Typography>
                    ) : (
                        <>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                                {contributionMarginData?.portfolio?.contribution_margin_percentage !== undefined
                                    ? formatPercentage(contributionMarginData.portfolio.contribution_margin_percentage)
                                    : 'N/A'}
                            </Typography>
                            <Typography variant="body2">
                                {contributionMarginData?.portfolio?.overhead_multiplier
                                    ? `Overhead: ${contributionMarginData.portfolio.overhead_multiplier}x`
                                    : 'Overhead allocation: 1.4x'}
                            </Typography>
                        </>
                    )}
                </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                    <Tooltip title="Accounts Receivable Aging and DSO">
                        <Typography variant="h6">AR Aging</Typography>
                    </Tooltip>
                    {isCardLoading('arAging') ? (
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Loading...</Typography>
                    ) : arAgingData ? (
                        <>
                            <Typography
                                variant="h4"
                                sx={{ fontWeight: 'bold' }}
                                data-testid="kpi-ar-total"
                                data-kpi-key="total_ar"
                                data-kpi-source="financial-engine/ar-aging"
                            >
                                {formatCurrency(arAgingData.total_ar)}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" data-testid="kpi-ar-bucket-1_30">
                                    0–30: {formatCurrency(arAgingData.aging_buckets?.['1_30'])}
                                </Typography>
                                <br />
                                <Typography variant="caption" data-testid="kpi-ar-bucket-31_60">
                                    31–60: {formatCurrency(arAgingData.aging_buckets?.['31_60'])}
                                </Typography>
                                <br />
                                <Typography variant="caption" data-testid="kpi-ar-bucket-61_90">
                                    61–90: {formatCurrency(arAgingData.aging_buckets?.['61_90'])}
                                </Typography>
                                <br />
                                <Typography variant="caption" data-testid="kpi-ar-bucket-over_90">
                                    90+: {formatCurrency(arAgingData.aging_buckets?.over_90)}
                                </Typography>
                            </Box>
                        </>
                    ) : (
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                            N/A
                        </Typography>
                    )}
                </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Revenue Triad (ASC 606)</Typography>
                        {isCardLoading('revenueTriad') ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Loading...
                                </Typography>
                            </Box>
                        ) : revenueTriadData ? (
                            <Grid container spacing={2}>
                                <Grid size={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Tooltip title="Revenue recognized per ASC 606 standards based on performance obligations">
                                            <Typography variant="subtitle2" color="text.secondary">Recognized</Typography>
                                        </Tooltip>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                            {formatCurrency(revenueTriadData.revenue_recognized)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Tooltip title="Total value of invoices sent to clients">
                                            <Typography variant="subtitle2" color="text.secondary">Invoiced</Typography>
                                        </Tooltip>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                                            {formatCurrency(revenueTriadData.revenue_invoiced)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={4}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Tooltip title="Actual payments received from clients">
                                            <Typography variant="subtitle2" color="text.secondary">Cash Collected</Typography>
                                        </Tooltip>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            {formatCurrency(revenueTriadData.cash_collected)}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        ) : (
                            <EmptyChartState
                                title="No revenue triad data"
                                description="Generate invoices and recognize revenue to compare billed vs collected."
                                testId="revenue-triad-empty"
                            />
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6 }}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>Cash Flow (Next Months)</Typography>
                        {isCardLoading('cashFlow') ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={(cashFlowData?.projections || []).map((projection) => ({
                                    ...projection,
                                    outflowsAbs: Math.abs(projection.outflows),
                                }))}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <RechartsTooltip />
                                    <Legend />
                                    <Bar dataKey="inflows" fill="#4caf50" name="Inflows" />
                                    <Bar dataKey="outflowsAbs" fill="#f44336" name="Outflows" />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                    <Tooltip title="Direct expenses as a fraction of revenue">
                        <Typography variant="h6">Expense Ratio</Typography>
                    </Tooltip>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {isCardLoading('expenseRatios')
                            ? 'Loading...'
                            : expenseRatiosData
                                ? formatPercentage(expenseRatiosData.expense_ratio)
                                : 'N/A'}
                    </Typography>
                </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100' }}>
                    <Tooltip title="Variance from industry benchmarks">
                        <Typography variant="h6">Benchmark Variance</Typography>
                    </Tooltip>
                    {isCardLoading('benchmarkCompare') ? (
                        <Typography variant="body2">Loading...</Typography>
                    ) : benchmarkCompareData ? (
                        <>
                            <Typography variant="body2">
                                Utilization Δ: {formatNullableRate(benchmarkCompareData.utilization?.variance)}
                            </Typography>
                            <Typography variant="body2">
                                Margin Δ: {formatNullableRate(benchmarkCompareData.margin?.variance)}
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2">N/A</Typography>
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
};

export default FinancialEngagementKpiGrid;
